import {
  Button,
  createDataTableColumnHelper,
  DataTable,
  Link as UiLink,
  SearchField,
  Switch,
} from "@beweegbaas/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { adminSocialEmotionalOverviewQueryOptions } from "#/modules/sociaal-emotioneel.overview/query.social-emotional-overview";
import { TogglePublishButton } from "#/modules/sociaal-emotioneel.overview/toggle-publish-button";
import {
  getLeerjaarComboTranslation,
  getSociaalEmotioneelDomeinTranslation,
} from "#/modules/util.i18n/enum-mapping";
import {
  ADMIN_SOCIAL_EMOTIONAL_SEARCH_DEFAULTS,
  adminSocialEmotionalSearchSchema,
} from "#/modules/sociaal-emotioneel.overview/schema.search";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { FilterBar, type FilterBarFilter } from "#/components/filter-bar";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";
import { LeerjaarCombo, SociaalEmotioneelDomein } from "#/schema.nswag";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/admin/social-emotional/",
)({
  validateSearch: adminSocialEmotionalSearchSchema,
  // Keeps default filter/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(ADMIN_SOCIAL_EMOTIONAL_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; the query cache owns freshness (a deps change
  // still re-runs the loader for the new key).
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(adminSocialEmotionalOverviewQueryOptions(deps)),
  component: SociaalEmotioneelThemasAdminOverview,
  errorComponent: SocialEmotionalError,
});

function SocialEmotionalError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type ThemeRow = components["schemas"]["SociaalEmotioneelThemasReeksAdminOverviewModel"];

const helper = createDataTableColumnHelper<ThemeRow>();

const columns = helper.columns([
  helper.accessor("name", {
    header: () => m["common.fields.name"](),
    meta: { rowTarget: true },
    cell: ({ row, getValue }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/admin/social-emotional/$id/edit"
        params={(prev: Record<string, string>) => ({ ...prev, id: row.original.id })}
      >
        {getValue()}
      </UiLink>
    ),
  }),
  helper.accessor("schoolName", {
    header: () => m["common.fields.school"](),
    cell: ({ getValue }) => getValue() || m["common.labels.default_school"](),
  }),
  helper.accessor("domein", {
    header: () => m["sociaal_emotioneel_thema.overview.table.domein"](),
    cell: ({ getValue }) => getSociaalEmotioneelDomeinTranslation(getValue()),
  }),
  helper.accessor("leerjaarCombo", {
    header: () => m["common.leerjaar"](),
    cell: ({ getValue }) =>
      getValue()
        .map((combo) => getLeerjaarComboTranslation(combo))
        .join(", "),
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => (
      <>
        {row.original.canTogglePublished && (
          <TogglePublishButton reeksId={row.original.id} isPublished={row.original.isPublished} />
        )}
      </>
    ),
  }),
]);

function SociaalEmotioneelThemasAdminOverview() {
  const loaderData = Route.useLoaderData();
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, Route.useNavigate());

  // Reads live in TanStack Query so the publish-toggle's invalidation refreshes this list too;
  // `loaderData` is only the render fallback if the cache entry was GC'd.
  const overviewQuery = useQuery({
    ...adminSocialEmotionalOverviewQueryOptions(search),
    placeholderData: keepPreviousData,
  });
  const { socialEmotionalThemes, totalPages, totalCount } = overviewQuery.data ?? loaderData;

  const filterBarFilters: FilterBarFilter[] = [
    {
      key: "domein",
      label: m["sociaal_emotioneel_thema.overview.table.domein"](),
      options: Object.values(SociaalEmotioneelDomein).map((value) => ({
        value,
        label: getSociaalEmotioneelDomeinTranslation(value),
      })),
      selectedValues: search.domein,
      onValuesChange: (values) => overview.setFilter({ domein: values }),
      operator: search.domeinOperator,
      onOperatorChange: (operator) => overview.setFilter({ domeinOperator: operator }),
    },
    {
      key: "leerjaarCombo",
      label: m["common.leerjaar"](),
      options: Object.values(LeerjaarCombo)
        .filter((value) => value !== LeerjaarCombo.None)
        .map((value) => ({ value, label: getLeerjaarComboTranslation(value) })),
      selectedValues: search.leerjaarCombo,
      onValuesChange: (values) => overview.setFilter({ leerjaarCombo: values }),
      operator: search.leerjaarComboOperator,
      onOperatorChange: (operator) => overview.setFilter({ leerjaarComboOperator: operator }),
    },
    {
      key: "isPublished",
      label: "Gepubliceerd",
      options: [
        { value: "true", label: "Ja" },
        { value: "false", label: "Nee" },
      ],
      selectedValues: search.isPublished,
      onValuesChange: (values) => overview.setFilter({ isPublished: values }),
    },
  ];

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField className="w-auto grow md:max-w-xs" {...overview.searchFieldProps} />
        </Page.Header.SearchField>
        <Page.Header.Section className="gap-3">
          <FilterBar
            filters={filterBarFilters}
            onClearAll={() =>
              overview.setFilter({
                domein: [],
                domeinOperator: "in",
                leerjaarCombo: [],
                leerjaarComboOperator: "in",
                isPublished: [],
              })
            }
          />
          <Switch
            id="showSchoolThemas"
            name={"showSchoolThemas"}
            checked={search.includeSchools}
            onCheckedChange={(checked) => overview.setFilter({ includeSchools: checked })}
          />
          <label htmlFor="showSchoolThemas" className="text-sm">
            Toon school thema's
          </label>
        </Page.Header.Section>
        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/admin/social-emotional/add"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              {m["common.actions.add"]()}
              <IconMaterialSymbolsAdd />
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label={m["common.labels.sociaal_emotionele_themas"]()}
          columns={columns}
          data={socialEmotionalThemes}
          getRowId={(theme) => theme.id.toString()}
          empty={m["admin.geen_themas_gevonden"]()}
          getRowClassName={(theme) =>
            !theme.isPublished ? "text-muted-foreground line-through" : undefined
          }
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"mb-6 border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
